/**
 * The typed client for the Worker's `/api/*` surface.
 *
 * Every response body here is a DTO from `@xtract-bot/embellisher-schema` —
 * the same declarations the Worker builds its responses from — so a route that
 * changes shape breaks this file at compile time rather than in a browser.
 * The three shapes the schema package does not declare (the session probe, the
 * consent lookup and the rollback receipt) are declared below, next to the
 * calls that read them.
 *
 * `credentials: "same-origin"` everywhere: the account cookie is HttpOnly and
 * SameSite=Lax, and the Worker refuses a cookie-authenticated write whose
 * `Origin` does not match, which `fetch` sets for us.
 */

import type {
  AccessRuleAdded,
  AccessSettings,
  AccountInfo,
  ActivityPage,
  AdminEntry,
  AdminRole,
  AdminStats,
  AdminUserDetail,
  AdminUserPage,
  ApiError,
  FeatureShare,
  FeatureShareAccepted,
  FeatureSharePage,
  ConfigBundle,
  ConfigVersionSummary,
  ConnectInfo,
  ConnectionInfo,
  DeviceInfo,
  ExtensionInstallInfo,
  ExtensionRelease,
  Invitation,
  InvitationSent,
  InvitationsPage,
  InviterEntry,
  LogicModuleInfo,
  MeResponse,
  OAuthProviderId,
} from "@xtract-bot/embellisher-schema";

/** `GET /api/session` — who is signed in, and which providers this deployment can sign them in with. */
export interface SessionResponse {
  account: AccountInfo | null;
  providers: OAuthProviderId[];
}

/** `GET /api/oauth/consent` — what the consent screen shows about the client asking. */
export interface ConsentResponse {
  clientName: string;
  clientUri: string | null;
  redirectHost: string;
  account: AccountInfo;
}

/** `POST /api/bundle/rollback` */
export interface RollbackResponse {
  version: number;
  restoredFrom: number;
  featureCount: number;
}

/**
 * A failure the UI can render: the Worker's own `{error, message}` when it sent
 * one, and a plain-language stand-in when it could not (the network, or a
 * proxy answering with HTML).
 */
export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiFailure";
    this.status = status;
    this.code = code;
  }

  /** True when signing in again is the fix — the caller usually redirects rather than showing an alert. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

async function failureFrom(response: Response): Promise<ApiFailure> {
  let body: Partial<ApiError> | null = null;
  try {
    body = (await response.json()) as Partial<ApiError>;
  } catch {
    // Not JSON: a proxy, or a route that answered HTML. Fall through.
  }
  if (body && typeof body.message === "string") {
    return new ApiFailure(response.status, typeof body.error === "string" ? body.error : "error", body.message);
  }
  return new ApiFailure(
    response.status,
    "unexpected_response",
    `The server answered ${response.status} without an explanation. Reload the page; if it keeps happening the deployment may be mid-update.`,
  );
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? "GET",
      credentials: "same-origin",
      ...(options.body === undefined ? {} : { headers: { "content-type": "application/json" }, body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiFailure(0, "network", "The server could not be reached. Check your connection and try again.");
  }
  if (!response.ok) throw await failureFrom(response);
  return response;
}

async function json<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await send(path, options)).json() as Promise<T>;
}

async function text(path: string, options: RequestOptions = {}): Promise<string> {
  return (await send(path, options)).text();
}

/** A raw body, for the one upload the web app makes (an extension build zip). */
async function upload<T>(path: string, body: Blob, contentType: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { method: "PUT", credentials: "same-origin", headers: { "content-type": contentType }, body });
  } catch {
    throw new ApiFailure(0, "network", "The server could not be reached. Check your connection and try again.");
  }
  if (!response.ok) throw await failureFrom(response);
  return response.json() as Promise<T>;
}

export const api = {
  // ── Session ─────────────────────────────────────────────────────────────
  session: (signal?: AbortSignal): Promise<SessionResponse> => json("/api/session", signal ? { signal } : {}),
  me: (): Promise<MeResponse> => json("/api/me"),
  /**
   * Signs out through `fetch` rather than a form so the browser sends an
   * `Origin` header the Worker's cross-origin check accepts. The 302 it
   * answers with is followed and discarded; the caller navigates.
   */
  signOut: async (): Promise<void> => {
    await send("/auth/signout", { method: "POST" });
  },

  // ── Connect ─────────────────────────────────────────────────────────────
  connect: (): Promise<ConnectInfo> => json("/api/connect"),
  consent: (search: string): Promise<ConsentResponse> => json(`/api/oauth/consent${search}`),

  // ── Configuration ───────────────────────────────────────────────────────
  config: (): Promise<ConfigBundle> => json("/api/bundle"),
  configVersions: (): Promise<ConfigVersionSummary[]> => json("/api/bundle/versions"),
  configVersion: (version: number): Promise<ConfigBundle> => json(`/api/bundle/versions/${version}`),
  rollback: (version: number): Promise<RollbackResponse> => json("/api/bundle/rollback", { method: "POST", body: { version } }),

  // ── Logic modules ───────────────────────────────────────────────────────
  logic: (): Promise<LogicModuleInfo[]> => json("/api/logic"),
  logicSource: (moduleId: string): Promise<string> => text(`/api/logic/${encodeURIComponent(moduleId)}/source`),

  // ── Activity ────────────────────────────────────────────────────────────
  activity: (limit = 25, signal?: AbortSignal): Promise<ActivityPage> =>
    json(`/api/activity?limit=${limit}`, signal ? { signal } : {}),

  // ── Account ─────────────────────────────────────────────────────────────
  devices: (): Promise<DeviceInfo[]> => json("/api/account/devices"),
  revokeDevice: (deviceId: string): Promise<DeviceInfo | null> =>
    json(`/api/account/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" }),
  connections: (): Promise<ConnectionInfo[]> => json("/api/account/connections"),
  revokeConnection: (authId: string): Promise<ConnectionInfo | null> =>
    json(`/api/account/connections/${encodeURIComponent(authId)}`, { method: "DELETE" }),

  // ── Sharing a feature ───────────────────────────────────────────────────
  shares: (): Promise<FeatureSharePage> => json("/api/shares"),
  share: (featureId: string, toEmail: string, note?: string): Promise<FeatureShare> =>
    json("/api/shares", { method: "POST", body: { featureId, toEmail, ...(note ? { note } : {}) } }),
  acceptShare: (shareId: string): Promise<FeatureShareAccepted> =>
    json(`/api/shares/${encodeURIComponent(shareId)}/accept`, { method: "POST" }),
  declineShare: (shareId: string): Promise<{ ok: true }> =>
    json(`/api/shares/${encodeURIComponent(shareId)}/decline`, { method: "POST" }),
  withdrawShare: (shareId: string): Promise<{ ok: true }> =>
    json(`/api/shares/${encodeURIComponent(shareId)}`, { method: "DELETE" }),

  // ── Inviting people, and the extension they install ─────────────────────
  invitations: (): Promise<InvitationsPage> => json("/api/invitations"),
  invite: (email: string, note?: string): Promise<InvitationSent> =>
    json("/api/invitations", { method: "POST", body: { email, ...(note ? { note } : {}) } }),
  resendInvitation: (inviteId: string): Promise<InvitationSent> =>
    json(`/api/invitations/${encodeURIComponent(inviteId)}/resend`, { method: "POST" }),
  withdrawInvitation: (inviteId: string): Promise<Invitation> =>
    json(`/api/invitations/${encodeURIComponent(inviteId)}`, { method: "DELETE" }),
  extension: (): Promise<ExtensionInstallInfo> => json("/api/extension"),

  // ── Admin ───────────────────────────────────────────────────────────────
  adminStats: (): Promise<AdminStats> => json("/api/admin/stats"),
  adminUsers: (query: { q?: string; cursor?: string; limit?: number } = {}): Promise<AdminUserPage> => {
    const params = new URLSearchParams();
    if (query.q) params.set("q", query.q);
    if (query.cursor) params.set("cursor", query.cursor);
    if (query.limit !== undefined) params.set("limit", String(query.limit));
    const search = params.toString();
    return json(`/api/admin/users${search ? `?${search}` : ""}`);
  },
  adminUser: (accountId: string): Promise<AdminUserDetail> => json(`/api/admin/users/${encodeURIComponent(accountId)}`),
  adminSetDisabled: (accountId: string, disabled: boolean): Promise<AccountInfo | null> =>
    json(`/api/admin/users/${encodeURIComponent(accountId)}/${disabled ? "disable" : "enable"}`, { method: "POST" }),
  adminRevokeDevice: (accountId: string, deviceId: string): Promise<DeviceInfo | null> =>
    json(`/api/admin/users/${encodeURIComponent(accountId)}/devices/${encodeURIComponent(deviceId)}/revoke`, { method: "POST" }),
  adminRevokeConnection: (accountId: string, authId: string): Promise<ConnectionInfo | null> =>
    json(`/api/admin/users/${encodeURIComponent(accountId)}/connections/${encodeURIComponent(authId)}/revoke`, { method: "POST" }),
  adminShareFeature: (accountId: string, featureId: string, toEmail: string, note?: string): Promise<FeatureShare> =>
    json(`/api/admin/users/${encodeURIComponent(accountId)}/shares`, { method: "POST", body: { featureId, toEmail, ...(note ? { note } : {}) } }),
  adminAdmins: (): Promise<AdminEntry[]> => json("/api/admin/admins"),
  adminAddAdmin: (email: string, role: AdminRole): Promise<AdminEntry> =>
    json("/api/admin/admins", { method: "POST", body: { email, role } }),
  adminSetAdminRole: (email: string, role: AdminRole): Promise<AdminEntry> =>
    json(`/api/admin/admins/${encodeURIComponent(email)}`, { method: "PUT", body: { role } }),
  adminRemoveAdmin: (email: string): Promise<{ ok: true }> =>
    json(`/api/admin/admins/${encodeURIComponent(email)}`, { method: "DELETE" }),

  // ── Access (super-admin to write, any admin to read) ─────────────────────
  adminAccess: (): Promise<AccessSettings> => json("/api/admin/access"),
  adminSetAccessPolicy: (enforcing: boolean): Promise<AccessSettings> =>
    json("/api/admin/access", { method: "PUT", body: { enforcing } }),
  /** Adding an address also emails them setup instructions; the reply says whether it went. */
  adminAddAccessRule: (value: string, options: { note?: string; message?: string } = {}): Promise<AccessRuleAdded> =>
    json("/api/admin/access/rules", {
      method: "POST",
      body: { value, ...(options.note ? { note: options.note } : {}), ...(options.message ? { message: options.message } : {}) },
    }),
  adminRemoveAccessRule: (value: string): Promise<{ ok: true }> =>
    json(`/api/admin/access/rules/${encodeURIComponent(value)}`, { method: "DELETE" }),

  // ── Invitations (super-admin to write, any admin to read) ────────────────
  adminInviters: (): Promise<InviterEntry[]> => json("/api/admin/inviters"),
  adminAddInviter: (email: string): Promise<InviterEntry> => json("/api/admin/inviters", { method: "POST", body: { email } }),
  adminRemoveInviter: (email: string): Promise<{ ok: true }> =>
    json(`/api/admin/inviters/${encodeURIComponent(email)}`, { method: "DELETE" }),
  adminInvitations: (): Promise<Invitation[]> => json("/api/admin/invitations"),
  adminExtensions: (): Promise<ExtensionRelease[]> => json("/api/admin/extensions"),
  adminPublishExtension: (version: string, zip: Blob): Promise<ExtensionRelease> =>
    upload(`/api/admin/extensions/${encodeURIComponent(version)}`, zip, "application/zip"),
  adminDeleteExtension: (version: string): Promise<{ version: string; deleted: true }> =>
    json(`/api/admin/extensions/${encodeURIComponent(version)}`, { method: "DELETE" }),
};

/** The message to show for a thrown value, whatever it turns out to be. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiFailure) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}
